<template>
  <div ref="root">
    <section
      class="flex flex-col xl:flex-row xl:items-end xl:justify-between pb-8 gap-4"
    >
      <div class="flex flex-row flex-wrap items-center gap-2">
        <SearchableDropdown
          v-model="selectedRegion"
          :options="regions"
          :all-label="props.allRegionsTranslation"
        >
          <template #placeholder>
            <Translation
              name="calculation_wizard.benchmark_select.all_regions"
              default="All Regions"
            />
          </template>
        </SearchableDropdown>
        <div class="relative" ref="microRegionDropdown">
          <button
            @click="isMicroRegionDropdownOpen = !isMicroRegionDropdownOpen"
            class="filter-dropdown"
          >
            <span
              v-if="microRegionButtonLabel !== props.allMicroRegionsTranslation"
            >
              {{ microRegionButtonLabel }}</span
            >
            <Translation
              v-else
              name="calculation_wizard.benchmark_select.all_micro_regions"
              default="All Micro-regions"
            />
            <ChevronDown
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isMicroRegionDropdownOpen }"
            />
          </button>

          <div
            v-if="isMicroRegionDropdownOpen"
            class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
          >
            <ul class="max-h-60 overflow-auto text-sm">
              <li
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                @click="toggleAllMicroRegions"
              >
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="selectedMicroRegions.length === 0"
                    class="w-4 h-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                    @click.stop
                    @change="toggleAllMicroRegions"
                  />
                  {{ props.allMicroRegionsTranslation }}
                </label>
              </li>
              <li
                v-for="region in availableMicroRegions"
                :key="region"
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                :style="
                  selectedMicroRegions.includes(region)
                    ? { backgroundColor: '#EAF7F6' }
                    : {}
                "
                @click="toggleMicroRegion(region)"
              >
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="selectedMicroRegions.includes(region)"
                    class="w-4 h-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                    @click.stop
                    @change="toggleMicroRegion(region)"
                  />
                  {{ region }}
                </label>
              </li>
            </ul>
          </div>
        </div>
        <div class="relative" ref="yearDropdown">
          <button
            @click="isYearDropdownOpen = !isYearDropdownOpen"
            class="filter-dropdown"
          >
            <span v-if="yearButtonLabel !== props.allYearsTranslation">
              {{ yearButtonLabel }}</span
            >
            <Translation
              v-else
              name="calculation_wizard.benchmark_select.all_years"
              default="All Years"
            />
            <ChevronDown
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isYearDropdownOpen }"
            />
          </button>

          <div
            v-if="isYearDropdownOpen"
            class="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
          >
            <ul class="max-h-60 overflow-auto text-sm">
              <li
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                @click="toggleAllYears"
              >
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="selectedYears.length === 0"
                    class="w-4 h-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                    @click.stop
                    @change="toggleAllYears"
                  />
                  {{ props.allYearsTranslation }}
                </label>
              </li>
              <li
                v-for="year in availableYears"
                :key="year"
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                :style="
                  selectedYears.includes(year)
                    ? { backgroundColor: '#EAF7F6' }
                    : {}
                "
                @click="toggleYear(year)"
              >
                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    :checked="selectedYears.includes(year)"
                    class="w-4 h-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                    @click.stop
                    @change="toggleYear(year)"
                  />
                  {{ year }}
                </label>
              </li>
            </ul>
          </div>
        </div>
        <div class="relative" ref="methodologyDropdown">
          <button
            @click="isMethodologyDropdownOpen = !isMethodologyDropdownOpen"
            class="filter-dropdown"
          >
            <span
              v-if="
                selectedMethodology.label !== props.allMethodologiesTranslation
              "
            >
              {{ selectedMethodology.label }}</span
            >
            <Translation
              v-else
              name="calculation_wizard.benchmark_select.all_methodologies"
              default="All Methodologies"
            />
            <ChevronDown
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': isMethodologyDropdownOpen }"
            />
          </button>

          <div
            v-if="isMethodologyDropdownOpen"
            class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
          >
            <ul class="max-h-60 overflow-auto text-sm">
              <li
                v-for="method in methodologies"
                :key="method.value"
                :value="method.value"
                @click="
                  selectedMethodology = method;
                  isMethodologyDropdownOpen = false;
                "
                class="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between gap-2"
                :style="
                  method.value === selectedMethodology.value
                    ? { backgroundColor: '#EAF7F6' }
                    : {}
                "
              >
                {{ method.label }}
                <svg
                  v-if="method.value === selectedMethodology.value"
                  class="w-4 h-4 flex-shrink-0 text-brand-600"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M16.667 5L7.5 14.167L3.333 10" />
                </svg>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <div
      v-if="activeFilterPills.length > 0"
      class="flex flex-wrap items-center gap-2 pb-4"
    >
      <span class="text-sm text-gray-500">Selected</span>
      <span
        v-for="pill in activeFilterPills"
        :key="pill.key"
        class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-25 text-brand-700"
      >
        {{ pill.label }}
        <button @click="pill.remove" class="ml-0.5 hover:text-brand-900">
          &times;
        </button>
      </span>
    </div>
    <div class="lg:border border-solid border-gray-200 rounded-xl">
      <table class="w-full text-left">
        <thead class="hidden bg-gray-25 lg:table-header-group">
          <tr class="border-b border-solid border-gray-200">
            <th class="table-header rounded-tl-xl">
              <Translation name="calculation_wizard.region" default="Region" />
            </th>
            <th class="table-header">
              <Translation
                name="calculation_wizard.micro_region"
                default="Micro-region"
              />
            </th>
            <th class="table-header">
              <Translation
                name="calculation_wizard.availability"
                default="Availability"
              />
            </th>
            <th class="table-header">
              <Translation name="calculation_wizard.year" default="Year" />
            </th>
            <th class="table-header">
              <Translation
                name="calculation_wizard.methodology.label"
                default="Methodology"
              />
            </th>
            <th class="table-header rounded-tr-xl text-right"></th>
          </tr>
        </thead>
        <tbody class="flex flex-col gap-y-3 lg:table-row-group">
          <tr
            v-for="benchmark in filteredBenchmarks"
            :key="benchmark.id"
            class="grid table-grid p-4 lg:p-0 lg:table-row gap-4 hover:bg-gray-50 transition-all rounded-xl lg:rounded-none data-tr"
            :class="
              selectedBenchmarkId === benchmark.id
                ? 'row-selected-styles'
                : 'bg-white'
            "
          >
            <td
              class="col-span-3 text-gray-900 flex items-center justify-between lg:font-semibold lg:h-[72px] lg:px-4 xl:px-6 lg:table-cell"
              :class="
                filteredBenchmarks[filteredBenchmarks.length - 1] == benchmark
                  ? 'rounded-bl-xl'
                  : ''
              "
            >
              <div class="flex flex-col">
                <span
                  class="text-lg font-bold lg:text-sm lg:font-normal mb-1 lg:mb-0"
                >
                  {{ getDisplayRegionName(benchmark) }}</span
                >
                <span class="text-sm text-gray-600 font-normal lg:hidden">
                  {{ benchmark.micro_region }}
                </span>
              </div>
              <button
                class="lg:hidden rounded-lg text-sm px-3 py-2 font-normal transition-all hover:bg-brand-600 hover:text-white flex items-center gap-x-1"
                style="width: fit-content"
                :class="
                  selectedBenchmarkId === benchmark.id
                    ? 'bg-brand-600 text-white'
                    : 'bg-brand-25 text-brand-700'
                "
                @click="selectBenchmark(benchmark)"
              >
                <SimpleCheckMarkIcon
                  v-if="selectedBenchmarkId === benchmark.id"
                />
                <div>
                  <Translation
                    v-if="selectedBenchmarkId !== benchmark.id"
                    name="calculation_wizard.select_benchmark.select"
                    default="Select"
                  />
                  <Translation
                    v-else
                    name="calculation_wizard.select_benchmark.selected"
                    default="Selected"
                  />
                </div>
              </button>
            </td>
            <td class="hidden benchmark-select-cell">
              {{ benchmark.micro_region }}
            </td>
            <td class="col-span-1 flex flex-col benchmark-select-cell">
              <span class="benchmark-select-label">
                <Translation
                  name="calculation_wizard.availability"
                  default="Availability"
              /></span>
              <span v-if="benchmark.availability === 'For Purchase'">
                <Translation
                  name="calculation_wizard.benchmark_select.availability_for_purchase"
                  default="For Purchase"
                />
              </span>
              <span v-else>
                <Translation
                  name="calculation_wizard.benchmark_select.availability_public"
                  default="Public"
                />
              </span>
            </td>
            <td class="col-span-1 flex flex-col benchmark-select-cell">
              <span class="benchmark-select-label">
                <Translation name="calculation_wizard.year" default="Year"
              /></span>
              {{ benchmark.year }}
            </td>
            <td class="col-span-2 flex flex-col benchmark-select-cell">
              <span class="benchmark-select-label">
                <Translation
                  name="calculation_wizard.methodology.label"
                  default="Methodology"
              /></span>
              <div class="flex items-center gap-x-2">
                {{ methodologyName(benchmark.methodology) }}
              </div>
            </td>
            <td
              class="hidden benchmark-select-cell"
              :class="
                filteredBenchmarks[filteredBenchmarks.length - 1] == benchmark
                  ? 'rounded-br-xl'
                  : ''
              "
            >
              <button
                class="rounded-lg text-sm px-3 py-2 font-normal transition-all hover:bg-brand-600 hover:text-white flex items-center gap-x-1"
                style="width: fit-content"
                :class="
                  selectedBenchmarkId === benchmark.id
                    ? 'bg-brand-600 text-white'
                    : 'bg-brand-25 text-brand-700'
                "
                @click="selectBenchmark(benchmark)"
              >
                <SimpleCheckMarkIcon
                  v-if="selectedBenchmarkId === benchmark.id"
                />
                <div>
                  <Translation
                    v-if="selectedBenchmarkId !== benchmark.id"
                    name="calculation_wizard.select_benchmark.select"
                    default="Select"
                  />
                  <Translation
                    v-else
                    name="calculation_wizard.select_benchmark.selected"
                    default="Selected"
                  />
                </div>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import ChevronDown from "../icons/ChevronDown.vue";
import SimpleCheckMarkIcon from "../icons/SimpleCheckMarkIcon.vue";
import SearchableDropdown from "../SearchableDropdown.vue";

const props = defineProps({
  benchmarks: {
    type: Array,
    required: true,
  },
  initialBenchmarkId: {
    type: Number,
    required: false,
    default: null,
  },
  benchmarkYear: {
    type: [Number, String],
    required: true,
  },
  facilityIsoRegion: {
    type: String,
    required: false,
    default: null,
  },
  facilityRegionName: {
    type: String,
    required: false,
    default: null,
  },
  allRegionsTranslation: {
    type: String,
    required: true,
  },
  allMicroRegionsTranslation: {
    type: String,
    required: true,
  },
  allYearsTranslation: {
    type: String,
    required: true,
  },
  allMethodologiesTranslation: {
    type: String,
    required: true,
  },
});

const methodologyName = (methodology) => {
  switch (methodology) {
    case "anker":
      return "Full-Fledged Anker Methodology";
    case "living-wage-us":
      return "Living Wage US Methodology";
    case "wage-indicator":
      return "WageIndicator Typical Family Methodology";
    case "fair-wage":
      return "Fair Wage Network Typical Family Methodology";
    case "other":
      return "Other";
    default:
      return "Other";
  }
};

const getDisplayRegionName = (benchmark) => {
  return benchmark.country_region || benchmark.iso_region_name;
};

const selectedBenchmarkId = ref(props.initialBenchmarkId);
const root = ref(null);

const selectBenchmark = (benchmark) => {
  selectedBenchmarkId.value = benchmark.id;

  const event = new CustomEvent("benchmark:selected", {
    detail: { benchmark: benchmark },
    bubbles: true,
  });
  root.value?.dispatchEvent(event);
};

const getInitialRegion = () => {
  if (props.facilityIsoRegion) {
    const matchByIso = props.benchmarks.find(
      (b) => b.iso_region === props.facilityIsoRegion,
    );
    if (matchByIso && matchByIso.iso_region_name) {
      return matchByIso.iso_region_name;
    }
  }

  if (props.facilityRegionName) {
    const regionNameLower = props.facilityRegionName.toLowerCase();

    const exactMatch = props.benchmarks.find(
      (b) => b.iso_region_name?.toLowerCase() === regionNameLower,
    );
    if (exactMatch) {
      return exactMatch.iso_region_name;
    }

    const partialMatch = props.benchmarks.find(
      (b) =>
        b.iso_region_name?.toLowerCase().includes(regionNameLower) ||
        regionNameLower.includes(b.iso_region_name?.toLowerCase() || ""),
    );
    if (partialMatch) {
      return partialMatch.iso_region_name;
    }
  }

  return props.allRegionsTranslation;
};

const getInitialYears = (forRegion) => {
  let benchmarksToCheck = props.benchmarks;
  if (forRegion && forRegion !== props.allRegionsTranslation) {
    benchmarksToCheck = benchmarksToCheck.filter(
      (b) => b.iso_region_name === forRegion,
    );
  }

  // Get available years as numbers for comparison
  const availableYears = benchmarksToCheck
    .map((b) => Number(b.year))
    .filter((y) => !isNaN(y) && y > 0);

  const reportingYear = Number(props.benchmarkYear);

  // Default filter for benchmark estimates is always the calculation (reporting) year
  if (reportingYear && availableYears.includes(reportingYear)) {
    return [reportingYear];
  }
  return [];
};

const initialRegion = getInitialRegion();
const selectedRegion = ref(initialRegion);

const microRegionDropdown = ref(null);
const selectedMicroRegions = ref([]); // Empty array means "All Micro-regions"
const isMicroRegionDropdownOpen = ref(false);

// Preferred micro-regions to pre-select when available
const preferredMicroRegions = [
  "Non-metropolitan-urban",
  "Non-metropolitan urban",
  "Non-Metropolitan Urban",
];

const yearDropdown = ref(null);
// Initialize year based on the pre-selected region
const selectedYears = ref(getInitialYears(initialRegion));
const isYearDropdownOpen = ref(false);

const methodologyDropdown = ref(null);
const selectedMethodology = ref({
  value: "all-methodologies",
  label: props.allMethodologiesTranslation,
});
const isMethodologyDropdownOpen = ref(false);

const handleClickOutside = (targetRef, callback) => {
  const handleClick = (e) => {
    if (targetRef.value && !targetRef.value.contains(e.target)) {
      callback();
    }
  };
  onMounted(() => document.addEventListener("click", handleClick));
  onUnmounted(() => document.removeEventListener("click", handleClick));
};

handleClickOutside(
  microRegionDropdown,
  () => (isMicroRegionDropdownOpen.value = false),
);
handleClickOutside(yearDropdown, () => (isYearDropdownOpen.value = false));
handleClickOutside(
  methodologyDropdown,
  () => (isMethodologyDropdownOpen.value = false),
);

const regions = computed(() => {
  const set = new Set(
    props.benchmarks
      .filter((b) => b.iso_region !== "COUNTRY_WIDE")
      .map((b) => b.iso_region_name)
      .filter(Boolean),
  );
  return [props.allRegionsTranslation, ...Array.from(set)];
});

const availableMicroRegions = computed(() => {
  let benchmarksToFilter = props.benchmarks;

  if (selectedRegion.value !== props.allRegionsTranslation) {
    benchmarksToFilter = benchmarksToFilter.filter(
      (b) => b.iso_region_name === selectedRegion.value,
    );
  }

  const set = new Set(
    benchmarksToFilter.map((b) => b.micro_region).filter(Boolean),
  );
  return Array.from(set).sort();
});

const availableYears = computed(() => {
  let benchmarksToFilter = props.benchmarks;

  if (selectedRegion.value !== props.allRegionsTranslation) {
    benchmarksToFilter = benchmarksToFilter.filter(
      (b) => b.iso_region_name === selectedRegion.value,
    );
  }

  const set = new Set(
    benchmarksToFilter.map((b) => Number(b.year)).filter(Boolean),
  );
  return Array.from(set).sort((a, b) => b - a);
});

// Label for the micro-region dropdown button
const microRegionButtonLabel = computed(() => {
  if (selectedMicroRegions.value.length === 0) {
    return props.allMicroRegionsTranslation;
  }
  if (selectedMicroRegions.value.length === 1) {
    return selectedMicroRegions.value[0];
  }
  return `${selectedMicroRegions.value.length} micro-regions`;
});

// Label for the micro-region dropdown button
const yearButtonLabel = computed(() => {
  if (selectedYears.value.length === 0) {
    return props.allYearsTranslation;
  }
  if (selectedYears.value.length === 1) {
    return selectedYears.value[0];
  }
  return `${selectedYears.value.length} years`;
});

// Toggle a single micro-region selection
const toggleMicroRegion = (region) => {
  const index = selectedMicroRegions.value.indexOf(region);
  if (index === -1) {
    selectedMicroRegions.value.push(region);
  } else {
    selectedMicroRegions.value.splice(index, 1);
  }
};

// Toggle "All Micro-regions" (clears selection)
const toggleAllMicroRegions = () => {
  selectedMicroRegions.value = [];
};

// Toggle a single year selection
const toggleYear = (year) => {
  const index = selectedYears.value.indexOf(year);
  if (index === -1) {
    selectedYears.value.push(year);
  } else {
    selectedYears.value.splice(index, 1);
  }
};

// Toggle "All Years" (clears selection)
const toggleAllYears = () => {
  selectedYears.value = [];
};

// Pre-select preferred micro-regions when region changes
const preselectMicroRegions = () => {
  const available = availableMicroRegions.value;
  const toSelect = preferredMicroRegions.filter((mr) => available.includes(mr));

  if (toSelect.length > 0) {
    selectedMicroRegions.value = toSelect;
  } else {
    selectedMicroRegions.value = [];
  }
};

// Watch for region changes to update micro-region and year pre-selection
watch(selectedRegion, (newRegion) => {
  preselectMicroRegions();
  // Also update year to reporting year and -1 if available for this region
  const yearForRegion = getInitialYears(newRegion);
  selectedYears.value = yearForRegion;
});

// Initial pre-selection of micro-regions
preselectMicroRegions();

const methodologies = computed(() => {
  const set = new Set([
    "all-methodologies",
    ...props.benchmarks.map((b) => b.methodology).filter(Boolean),
  ]);

  const arrayFromSet = Array.from(set).map((m) => ({
    value: m,
    label: m
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" "),
  }));
  return arrayFromSet;
});

const activeFilterPills = computed(() => {
  const pills = [];
  if (selectedRegion.value !== props.allRegionsTranslation) {
    pills.push({
      key: "region",
      label: selectedRegion.value,
      remove: () => {
        selectedRegion.value = props.allRegionsTranslation;
      },
    });
  }
  for (const mr of selectedMicroRegions.value) {
    pills.push({
      key: "micro-" + mr,
      label: mr,
      remove: () => {
        toggleMicroRegion(mr);
      },
    });
  }
  for (const yr of selectedYears.value) {
    pills.push({
      key: "year-" + yr,
      label: String(yr),
      remove: () => {
        toggleYear(yr);
      },
    });
  }
  if (selectedMethodology.value.value !== "all-methodologies") {
    pills.push({
      key: "methodology",
      label: selectedMethodology.value.label,
      remove: () => {
        selectedMethodology.value = {
          value: "all-methodologies",
          label: props.allMethodologiesTranslation,
        };
      },
    });
  }
  return pills;
});

const filteredBenchmarks = computed(() => {
  let results;

  if (
    selectedRegion.value === props.allRegionsTranslation &&
    selectedMicroRegions.value.length === 0 &&
    selectedYears.value.length === 0 &&
    selectedMethodology.value.value === "all-methodologies"
  ) {
    results = [...props.benchmarks];
  } else {
    results = props.benchmarks.filter((b) => {
      // When a specific region is selected, also include country-wide benchmarks
      const isCountryWide = b.iso_region === "COUNTRY_WIDE";
      const regionMatch =
        selectedRegion.value === props.allRegionsTranslation ||
        b.iso_region_name === selectedRegion.value ||
        (selectedRegion.value !== props.allRegionsTranslation && isCountryWide);

      // Multi-select: empty array means "all", otherwise check if included
      const microRegionMatch =
        selectedMicroRegions.value.length === 0 ||
        selectedMicroRegions.value.includes(b.micro_region);

      const yearMatch =
        selectedYears.value.length === 0 ||
        selectedYears.value.includes(Number(b.year));

      const methodologyMatch =
        selectedMethodology.value.value === "all-methodologies" ||
        b.methodology === selectedMethodology.value.value;

      return regionMatch && microRegionMatch && yearMatch && methodologyMatch;
    });
  }

  const reportingYear = Number(props.benchmarkYear);
  return results.sort((a, b) => {
    const aMatchesYear = Number(a.year) === reportingYear ? 0 : 1;
    const bMatchesYear = Number(b.year) === reportingYear ? 0 : 1;
    if (aMatchesYear !== bMatchesYear) return aMatchesYear - bMatchesYear;

    const yearDiff = (Number(b.year) || 0) - (Number(a.year) || 0);
    if (yearDiff !== 0) return yearDiff;

    const aIsCountryWide = a.iso_region === "COUNTRY_WIDE" ? 1 : 0;
    const bIsCountryWide = b.iso_region === "COUNTRY_WIDE" ? 1 : 0;
    return aIsCountryWide - bIsCountryWide;
  });
});
</script>
<style>
.row-selected-styles {
  background-color: #eaf7f6;
  border-color: #265957;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  @media (min-width: 1024px) {
    background-color: white;
    border-color: #e9eaeb;
    box-shadow: none;
  }
}
</style>
